import { LRUCache } from 'lru-cache';
import { queryPnlReport } from 'src/components/star-grid/services/starGridService';
import {
  PNL_SNAPSHOT_CACHE_BYTES,
  PNL_SNAPSHOT_CACHE_ENTRIES,
  PNL_SNAPSHOT_CACHE_TTL_MS,
  PNL_SNAPSHOT_PENDING_LIMIT
} from 'src/components/star-grid/starGridConstants';
import { PnlQueryRequest, PnlQueryResponse, PnlReportConfig } from 'src/components/star-grid/starGridTypes';
import { awaitWithAbort } from 'src/components/star-grid/utils/awaitWithAbort';
import { pnlSnapshotCacheKey } from 'src/components/star-grid/utils/pnlSnapshotCacheKey';

interface SnapshotQuery {
  readonly reportId: number;
  readonly request: PnlQueryRequest;
  readonly definition?: PnlReportConfig;
  readonly signal: AbortSignal;
  readonly bypassCache?: boolean;
}
interface PendingSnapshot {
  readonly controller: AbortController;
  readonly promise: Promise<PnlQueryResponse>;
}
interface SnapshotCacheState {
  readonly fetcher: typeof queryPnlReport;
  readonly results: LRUCache<string, PnlQueryResponse>;
  readonly pending: LRUCache<string, PendingSnapshot>;
}
export interface PnlSnapshotCache {
  readonly query: (query: SnapshotQuery) => Promise<PnlQueryResponse>;
  readonly clear: () => void;
}

const clearSnapshots = (cache: SnapshotCacheState): void => {
  cache.pending.forEach(({ controller }) => controller.abort());
  cache.pending.clear();
  cache.results.clear();
};

const startSnapshot = (cache: SnapshotCacheState, key: string, query: SnapshotQuery): PendingSnapshot => {
  const controller = new AbortController();
  const promise = cache
    .fetcher(query.reportId, query.request, controller.signal)
    .then((response) => {
      if (!controller.signal.aborted) cache.results.set(key, response);
      return response;
    })
    .finally(() => {
      if (cache.pending.peek(key)?.controller === controller) cache.pending.delete(key);
    });
  const pending = { controller, promise };
  cache.pending.set(key, pending);
  return pending;
};

const querySnapshot = async (cache: SnapshotCacheState, query: SnapshotQuery): Promise<PnlQueryResponse> => {
  if (query.signal.aborted) throw new DOMException('Aborted', 'AbortError');
  if (query.bypassCache) clearSnapshots(cache);
  if (!query.request.as_of_timestamp) return cache.fetcher(query.reportId, query.request, query.signal);
  const key = pnlSnapshotCacheKey(query.reportId, query.request, query.definition);
  const response = cache.results.get(key);
  if (response) return response;
  const pending = cache.pending.get(key) ?? startSnapshot(cache, key, query);
  return awaitWithAbort(pending.promise, query.signal);
};

const snapshotResults = (): SnapshotCacheState['results'] =>
  new LRUCache({
    max: PNL_SNAPSHOT_CACHE_ENTRIES,
    maxSize: PNL_SNAPSHOT_CACHE_BYTES,
    ttl: PNL_SNAPSHOT_CACHE_TTL_MS,
    sizeCalculation: (response) => JSON.stringify(response).length * 2
  });

/** Owned by the report view; financial data stays in memory and is cleared on unmount. */
export const createPnlSnapshotCache = (fetcher = queryPnlReport): PnlSnapshotCache => {
  const cache: SnapshotCacheState = {
    fetcher,
    results: snapshotResults(),
    pending: new LRUCache({
      max: PNL_SNAPSHOT_PENDING_LIMIT,
      dispose: ({ controller }, _key, reason) => {
        if (reason === 'evict') controller.abort();
      }
    })
  };
  return { query: (query) => querySnapshot(cache, query), clear: () => clearSnapshots(cache) };
};
